import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { createContext, use, ReactNode, useMemo } from "react";
import {
  ChemicalElementSymbol,
  NonElementQuantity,
} from "src/store/api/generatedApi";
import { pickColorById } from "src/utils/colors";

interface ChemistryElementContextType {
  selectedElement: ChemicalElementSymbol | NonElementQuantity | undefined;
  setSelectedElement: (
    element: ChemicalElementSymbol | NonElementQuantity
  ) => void;
  availableElements: (ChemicalElementSymbol | NonElementQuantity)[];
  selectedElementColor: string;
}

const ChemistryElementContext = createContext<
  ChemistryElementContextType | undefined
>(undefined);

interface ChemistryElementProviderProps {
  children: ReactNode;
  availableElements: (ChemicalElementSymbol | NonElementQuantity)[];
}

/**
 * Keeps the previously-selected element while it remains available, otherwise
 * falls back to the first available element, which the backend returns in
 * per-tenant priority order.
 */
const resolveSelectedElement = (
  selectedElementParam: ChemicalElementSymbol | NonElementQuantity | null,
  availableElements: (ChemicalElementSymbol | NonElementQuantity)[]
): ChemicalElementSymbol | NonElementQuantity | undefined =>
  selectedElementParam !== null &&
  availableElements.includes(selectedElementParam)
    ? selectedElementParam
    : availableElements[0];

export const ChemistryElementProvider = ({
  children,
  availableElements,
}: ChemistryElementProviderProps) => {
  const [selectedElementParam, setSelectedElement] = useStringQueryParamState<
    ChemicalElementSymbol | NonElementQuantity | null
  >("element", null);

  const selectedElement = resolveSelectedElement(
    selectedElementParam,
    availableElements
  );

  const value = useMemo(
    () => ({
      selectedElement,
      setSelectedElement,
      availableElements,
      selectedElementColor: pickColorById(
        selectedElement ? availableElements.indexOf(selectedElement) : -1
      ),
    }),
    [selectedElement, availableElements, setSelectedElement]
  );

  return (
    <ChemistryElementContext value={value}>{children}</ChemistryElementContext>
  );
};

export const useChemistryElement = () => {
  const context = use(ChemistryElementContext);
  if (context === undefined) {
    throw new Error(
      "useChemistryElement must be used within a ChemistryElementProvider"
    );
  }
  return context;
};
